// This composable is used for detecting the opening of a selector menu and positioning it accordingly
// as well as accounting for page resizing and scrolling. It also uses a randomly generated ID and the
// 'selector' Vuex module to maintain that only one selector is ever open at a time.
import { useStore } from 'vuex';
import { ref, computed, watch, nextTick } from 'vue';
import Random from 'lib/random';

const useSelectorPositioning = ({ selectorEl, paywall, overrides }) => {
  const { state, commit, dispatch } = useStore();

  // Selector Menu Opening
  const selectorId = ref(Random.uuid());
  const selectorMenuIsOpen = computed(() => state.selector.openSelectorId === selectorId.value);
  const onSelectorButtonClick = () => {
    // Intercept any clicks on a disabled selector-button
    if (selectorEl.value.classList.contains('disabled')) return;
    dispatch('openUpgradeModalIfRequired', paywall)
      .then((upgradeIsRequired) => {
        if (!upgradeIsRequired) {
          commit('update', { module: 'selector', key: 'openSelectorId', value: selectorId.value });
        }
      });
  };

  // Menu Resizing and Positioning
  const selectorMenuWidth = ref(0);
  const selectorMenuPosition = ref({ top: 0, left: 0 });
  const selectorMenuStyle = computed(() => {
    const style = selectorMenuPosition.value;
    style.width = `${selectorMenuWidth.value}px`;
    return style;
  });
  const calculateSelectorMenuWidth = () => {
    if (overrides?.fixedWidth) {
      selectorMenuWidth.value = overrides.fixedWidth;
    } else {
      // The trigger element can be unmounted while resize/scroll listeners are still
      // attached (e.g. navigation with the menu open), so bail out if it's gone.
      if (!selectorEl.value) return;
      const rect = selectorEl.value.getBoundingClientRect();
      selectorMenuWidth.value = rect.right - rect.left;
    }
  };
  const calculateSelectorMenuPosition = () => {
    if (!selectorEl.value) return;
    const rect = selectorEl.value.getBoundingClientRect();
    let { left } = rect;
    if (overrides?.rightAlign) {
      // Right side of the selector button, moved to the left the full width of the menu
      left = rect.right - selectorMenuWidth.value;
    }
    selectorMenuPosition.value = { top: rect.bottom + 0, left };
  };
  const recalculateSelectorMenuLayout = () => {
    // On resize the trigger element's width can change in fluid layouts, so recompute
    // the width before the position (position may depend on the latest width).
    calculateSelectorMenuWidth();
    calculateSelectorMenuPosition();
  };
  const closeSelectorMenu = () => {
    commit('update', { module: 'selector', key: 'openSelectorId', value: null });
  };
  watch(selectorMenuIsOpen, (newValue) => {
    nextTick(() => {
      if (newValue) {
        // selectorMenuIsOpen is true, we've just opened the menu
        calculateSelectorMenuWidth();
        calculateSelectorMenuPosition();
        window.addEventListener('click', closeSelectorMenu);
        window.addEventListener('scroll', calculateSelectorMenuPosition, true);
        window.addEventListener('resize', recalculateSelectorMenuLayout);
      } else {
        // selectorMenuIsOpen is false, we've just closed the menu
        window.removeEventListener('click', closeSelectorMenu);
        window.removeEventListener('scroll', calculateSelectorMenuPosition, true);
        window.removeEventListener('resize', recalculateSelectorMenuLayout);
      }
    });
  });

  return {
    onSelectorButtonClick,
    selectorMenuIsOpen,
    selectorMenuStyle,
    calculateSelectorMenuPosition,
  };
};

export default useSelectorPositioning;
